import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search } from "carbon-components-react";
import { isProduction } from "config";

import ActionsList from "./ActionsList";
import AddFilterAction from "./AddFilterAction";
import AddSortAction from "./AddSortAction";
import EditColumns from "./EditColumns";
import PipelineTable from "./PipelineTable";
import CreateNewViewModal from "./CreateNewViewModal";
import SaveViewEditsModal from "./SaveViewEditsModal";
import DeleteViewModal from "./DeleteViewModal";

import Timer from "./Timer";
import {
  ActionDef,
  ActionKind,
  ColumnDef,
  ColumnDisplay,
  Data,
  HydratedAction,
  HydratedFilterAction,
  HydratedSortAction,
  isFilterAction,
  isSortAction,
  Schema,
  SortOrderKind,
} from "./shared";
import { hydrateActions } from "./serializer";
import {
  columnIsVisible,
  getColumnDefByJsonKey,
  getColumnDefFromDisplayName,
  formattedValue,
} from "./helpers";
import DownloadCSVButton from "./DownloadCSV";

const styles = require("./styles.module.scss");

const initialColumnDisplay = (s: Schema, initialColumns: string[]): ColumnDisplay[] => {
  if (!initialColumns || !initialColumns.length) {
    return s.map((d: ColumnDef) => ({
      jsonKey: d.jsonKey,
      visible: true,
    }));
  }

  const visible = initialColumns.map((jsonKey: string) => ({ jsonKey, visible: true }));
  const notVisible = s
    .filter((d: ColumnDef) => !initialColumns.includes(d.jsonKey))
    .map((d: ColumnDef) => ({
      jsonKey: d.jsonKey,
      visible: false,
    }));

  return [...visible, ...notVisible]; // visible columns first by convention
};

export const applyActions = (
  s: Schema,
  d: Data,
  cd: ColumnDisplay[],
  allActions: HydratedAction[],
): Data => {
  const actionsToApply = allActions.filter(({ jsonKey }) => columnIsVisible(cd, jsonKey));

  let applied = d;
  actionsToApply.filter(isFilterAction).forEach(action => {
    const { func } = action;
    applied = func(applied);
  });

  const sortKeys = actionsToApply.filter(isSortAction).map(({ jsonKey, sortOrder }) => {
    const { orderFunc } = getColumnDefByJsonKey(s, jsonKey);
    return { jsonKey, orderFunc, sortOrder };
  });

  if (sortKeys.length) {
    applied = applied.sort((a, b) => {
      for (let i = 0; i < sortKeys.length; i++) {
        const sort = sortKeys[i];
        let result: number;

        if (sort.sortOrder === SortOrderKind.Ascending) {
          result = sort.orderFunc(a[sort.jsonKey], b[sort.jsonKey]);
        } else {
          result = sort.orderFunc(b[sort.jsonKey], a[sort.jsonKey]);
        }

        if (result !== 0) {
          return result;
        }
      }

      return 0;
    });
  }

  return applied;
};

// exporting applySearchFilter for test purposes
export const applySearchFilter = (
  schema: Schema,
  data: Data,
  visibleKeys: string[],
  rawSearchText: string,
): Data => {
  const searchText = rawSearchText.toLowerCase().trim();
  if (searchText === "") {
    return data;
  }

  return data.filter(dataRow => {
    for (let i = 0; i < visibleKeys.length; i++) {
      const jsonKey = visibleKeys[i];
      const rawValue = dataRow[jsonKey];
      if (rawValue !== null) {
        const columnDef = getColumnDefByJsonKey(schema, jsonKey);
        const { kind, enums } = columnDef;
        const cellValue = formattedValue(kind, rawValue, enums);
        if (typeof cellValue !== "string") {
          if (!isProduction()) {
            console.warn(`Given jsonkey: "${jsonKey}" had a non-string value of: "${cellValue}"`);
          }
          return false;
        }

        const cellText = cellValue.toString().toLowerCase().trim();
        if (cellText.includes(searchText)) {
          return true;
        }
      }
    }
    return false;
  });
};

interface PipelineProps {
  title: string;
  schema: Schema;
  data: Data;
  lastLoaded: Number;
  initialColumns?: string[];
  initialActions?: ActionDef[];
  linkTo?: string;
  linkText?: string;
  userCanEditLoanApp: boolean;
}

const PipelineViewer = ({
  title,
  schema,
  data,
  lastLoaded,
  initialColumns,
  initialActions,
  linkTo,
  linkText,
  userCanEditLoanApp,
}: PipelineProps): JSX.Element => {
  const [searchText, setSearchText] = useState("");
  const [hasEdits, setHasEdits] = useState(false);
  useEffect(() => {
    setHasEdits(false);
  }, [title]);

  /*
   * Hooks: Columns
   */
  const [columnDisplay, setColumnDisplay] = useState<ColumnDisplay[]>(
    initialColumnDisplay(schema, initialColumns),
  );
  const visibleColumns = columnDisplay.filter(col => col.visible);
  const hiddenColumns = columnDisplay.filter(col => !col.visible);
  const visibleColumnKeys = visibleColumns.map(c => c.jsonKey);
  const visibleSchema = schema.filter(s => columnIsVisible(columnDisplay, s.jsonKey));

  /*
   * Handlers
   */
  const updateColumnDisplayHandler = (itemsToHide, itemsToShow) => {
    const visibleColDisplay = itemsToShow.map(displayName => {
      const { jsonKey } = getColumnDefFromDisplayName(schema, displayName);
      return {
        jsonKey,
        visible: true,
      };
    });

    const hiddenColDisplay = itemsToHide.map(displayName => {
      const { jsonKey } = getColumnDefFromDisplayName(schema, displayName);
      return {
        jsonKey,
        visible: false,
      };
    });

    setColumnDisplay([...visibleColDisplay, ...hiddenColDisplay]);
    setHasEdits(true);
  };

  /*
   * Hooks: Filter & Sort Actions
   */
  const [actions, setActions] = useState<HydratedAction[]>(hydrateActions(initialActions, schema));

  // Vars to hold the action's state while updating an action
  const [actionToUpdate, setActionToUpdate] = useState<HydratedAction>(null);
  const [openModalForActionKind, setOpenModalForActionKind] = useState<ActionKind>(null);

  const addAction = (newAction: HydratedAction) => {
    setActions([...actions, newAction]);
    setHasEdits(true);
  };

  const openUpdateModalForActionAtIndex = (idx: number) => {
    const toBeUpdated = actions[idx];
    setOpenModalForActionKind(toBeUpdated.kind);
    setActionToUpdate({ ...toBeUpdated, idx });
  };

  const updateActionAtIndex = (idx: number, action: HydratedAction) => {
    const withUpdatedAction = [...actions];
    withUpdatedAction.splice(idx, 1, action);
    setActions(withUpdatedAction);

    // Reset
    setOpenModalForActionKind(null);
    setActionToUpdate(null);

    setHasEdits(true);
  };

  const sortActionAtIndex = (key: string, sortStatus: string) => {
    const column = getColumnDefByJsonKey(schema, key);

    // check if the action is already applied, otherwise add it
    const actionIndex = actions.findIndex(
      ({ jsonKey }: HydratedAction) => column.jsonKey === jsonKey,
    );

    if (actionIndex >= 0) {
      const { kind = "sort" as ActionKind.Sort } = actions[actionIndex] as HydratedSortAction;

      if (sortStatus === null) {
        removeActionAtIndex(actionIndex);
      } else {
        const withUpdatedAction = [...actions];
        withUpdatedAction.splice(actionIndex, 1, {
          ...actions[actionIndex],
          kind,
          sortOrder: sortStatus,
          displayText: `${column.displayName}, ${sortStatus}`,
        } as HydratedAction);

        setActions(withUpdatedAction);
      }
    } else {
      addAction({
        kind: "sort",
        jsonKey: column.jsonKey,
        displayText: `${column.displayName}, ${sortStatus}`,
        sortOrder: sortStatus,
      } as HydratedAction);
    }
    // Reset
    setOpenModalForActionKind(null);
    setActionToUpdate(null);
    setHasEdits(true);
  };

  const removeActionAtIndex = (idx: number) => {
    const without: HydratedAction[] = [...actions];
    without.splice(idx, 1);
    setActions(without);

    setHasEdits(true);
  };

  const dataFilteredBySearch = applySearchFilter(schema, data, visibleColumnKeys, searchText);
  const results = applyActions(schema, dataFilteredBySearch, columnDisplay, actions);

  const viewSharedProps = {
    originalTitle: title,
    actions,
    columns: visibleColumnKeys,
  };

  return (
    <>
      <h3 style={{ marginBottom: 5 }}>Pipeline &ndash; {hasEdits ? `(Edited) ${title}` : title}</h3>
      <div className={styles.viewActionWrapper}>
        {userCanEditLoanApp && linkTo && linkText && (
          <Link to={linkTo}>
            <span className={styles.back}>{linkText}</span>
          </Link>
        )}
        <CreateNewViewModal {...viewSharedProps} />
        <SaveViewEditsModal {...viewSharedProps} afterSubmit={() => setHasEdits(false)} />
        <DeleteViewModal {...viewSharedProps} />
      </div>
      <div className={styles.actions}>
        <div style={{ maxWidth: 700 }}>
          <Search
            labelText="Search"
            placeholder="Search"
            size="lg"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>
        <div className={styles.actionsControls}>
          <EditColumns
            initialOptionsList={hiddenColumns.map(
              ({ jsonKey }) => getColumnDefByJsonKey(schema, jsonKey).displayName,
            )}
            initialSelectedList={visibleColumns.map(
              ({ jsonKey }) => getColumnDefByJsonKey(schema, jsonKey).displayName,
            )}
            onUpdate={updateColumnDisplayHandler}
          />
          <AddFilterAction
            schema={visibleSchema}
            addAction={addAction}
            actionToUpdate={actionToUpdate as HydratedFilterAction}
            updateAction={updateActionAtIndex}
            openModal={openModalForActionKind === ActionKind.Filter}
          />
          <AddSortAction
            schema={visibleSchema}
            addAction={addAction}
            actionToUpdate={actionToUpdate as HydratedSortAction}
            updateAction={updateActionAtIndex}
            openModal={openModalForActionKind === ActionKind.Sort}
          />
        </div>

        <div className={styles.actionsList}>
          <ActionsList
            actions={actions}
            columnDisplay={columnDisplay}
            openUpdateModalForActionAtIndex={openUpdateModalForActionAtIndex}
            removeActionAtIndex={removeActionAtIndex}
          />
        </div>
      </div>
      <div className={styles.results}>
        <p>
          Showing <span>{results.length}</span> row{results.length === 1 ? "" : "s"} in results
          &ndash; updated <Timer lastLoaded={lastLoaded} />
        </p>
        <DownloadCSVButton
          results={results}
          schema={schema}
          visibleColumnKeys={visibleColumnKeys}
        />
      </div>
      <div className={styles.sticky}>
        <PipelineTable
          rows={results.map(r => ({ ...r, id: `${r.loan_application_id}` }))}
          headers={visibleColumns.map(vc => ({
            id: `${vc.jsonKey}`,
            header: getColumnDefByJsonKey(schema, vc.jsonKey).displayName,
            key: vc.jsonKey,
            visible: true,
          }))}
          schema={schema}
          width={schema.length + 1}
          onSort={sortActionAtIndex}
          userCanEditLoanApp={userCanEditLoanApp}
          actions={actions}
        />
      </div>
    </>
  );
};

export default PipelineViewer;
